'use client';

import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import {
  User as FirebaseUser,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  updateProfile,
  reload,
} from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase/config';
import { AuthService, isFatalSyncErrorCode } from '@/services/auth.service';
import { ProfileService } from '@/services/profile.service';
import { getDeviceId } from '@/lib/device';
import { startDeviceHeartbeat } from '@/lib/device-heartbeat';
import { establishSession, endSession, isSessionCookieMissing, resolveCookieUser, SessionMintError } from '@/lib/session';
import { AUTH_FLOW_PATHS } from '@/lib/api/client';
import { shouldUseRedirect } from '@/lib/should-use-redirect';
import type { AudienceSegment } from '@/context/PreferencesContext';
import type { SeededUser } from '@/lib/seeded-user';
import * as Sentry from '@sentry/nextjs';

interface User {
  id: string;
  email: string | null;
  name: string | null;
  photoURL: string | null;
  role: 'STUDENT' | 'ADMIN' | 'INSTRUCTOR';
  onboardingCompleted: boolean;
  accountStatus: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'DELETED';
  deletionScheduledAt: Date | null;
  // Extended profile fields (loaded async after sync)
  /** @deprecated transition-period scalar mirror of targetExams[0] — kept for gradual migration, prefer targetExams. */
  targetExam?: string;
  targetExams?: string[];
  currentGrade?: string;
  /** Admin-managed education level tier slug (e.g. 'sma', 'smp'), or null/undefined if unset. */
  educationLevelSlug?: string | null;
  educationLevel?: { slug: string; name: string } | null;
  schoolName?: string;
  /** Coarse audience segment. `undefined` while the async profile fetch is still pending. */
  audienceSegment?: AudienceSegment | null;
}

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
  /**
   * True once Firebase has reported its first answer AND the signed-out branch (if taken) has
   * settled. A server-seeded user renders with `isLoading` false and `firebaseUser` null for
   * ~1.3s while Firebase restores its session, so `firebaseUser === null` alone cannot tell
   * "not reported yet" from "reported no user". Gate any "no Firebase user" state on this.
   */
  isFirebaseResolved: boolean;
  isAuthenticated: boolean;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (
    email: string,
    password: string,
    name: string,
    marketingConsent: boolean,
    birthYear: number | undefined,
    agreeTerms: boolean,
  ) => Promise<{ marketingConsentRefusalReason?: 'NOT_ADULT' }>;
  logout: () => Promise<void>;
  /**
   * For a user the cookie signs in but Firebase has no session for, and who needs one (an
   * action that reauthenticates). Ends THIS browser's cookie without revoking the account's
   * other sessions (unlike logout), marks the app signed out here, then goes to /login with
   * `returnTo` as its `?redirect=`. Rejects if the cookie could not be ended, without navigating.
   */
  signInAgain: (returnTo: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
  /**
   * True once a pending signInWithRedirect flow (see isStandaloneOrInAppBrowser) has been
   * picked up and completed via getRedirectResult on this page load. The click handler that
   * started the sign-in ran on a page that no longer exists after the redirect round trip,
   * so callers must watch this flag instead of awaiting loginWithGoogle() to know when to
   * navigate the user onward (e.g. back to checkout).
   */
  redirectSignInCompleted: boolean;
  /** Firebase error code from a redirect sign-in that completed but failed. Null otherwise. */
  redirectSignInErrorCode: string | null;
  markOnboardingComplete: () => void;
  resendVerificationEmail: () => Promise<void>;
  /** Reloads the Firebase user so `firebaseUser.emailVerified` reflects a verification done out of band. */
  refreshEmailVerified: () => Promise<void>;
  cancelDeletion: () => Promise<void>;
  /** Reflects a personalization change (segment/interests) into local user state without a full refetch. */
  updatePersonalization: (data: { audienceSegment?: AudienceSegment | null; targetExam?: string | null; targetExams?: string[] }) => void;
}

/**
 * Thrown by resendVerificationEmail() so callers can distinguish a rate-limited
 * request (HTTP 429 from the API's authLimiter, 10/min per user/IP — see
 * apps/api/src/app.ts) from other failures (email service down, etc.) and show
 * the right message / start a cooldown accordingly.
 */
export class ResendVerificationError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ResendVerificationError';
    this.status = status;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * True from a sign-out this app started itself (logout, a failed registration's cleanup, a
 * session the server refused) until Firebase next reports a user.
 *
 * With no Firebase user the session cookie decides who is signed in (see the listener), and
 * that must not undo a deliberate sign-out: endSession() is best-effort, so the cookie can
 * survive a logout. Module scope on purpose: the (auth) layout nests a second AuthProvider,
 * both listen to the same Firebase instance, and both must read the same answer.
 */
let signedOutHere = false;

function signOutHere(): Promise<void> {
  signedOutHere = true;
  return signOut(auth);
}

/**
 * Rehydrates the server-resolved user into the client-side `User` shape.
 *
 * Everything here came from GET /v1/users/me authenticated by the aks_session cookie, so it
 * is as trustworthy as what onAuthStateChanged's own syncUser call would return — it simply
 * arrives ~1.3s earlier. The extended profile fields (targetExams, currentGrade, ...) are
 * deliberately left undefined: they load in the non-blocking getProfile() call below, exactly
 * as they did before.
 */
function seedToUser(seed: SeededUser | null | undefined): User | null {
  if (!seed) return null;
  return {
    id: seed.id,
    email: seed.email,
    name: seed.name,
    photoURL: seed.photoURL,
    role: seed.role,
    onboardingCompleted: seed.onboardingCompleted,
    accountStatus: seed.accountStatus,
    deletionScheduledAt: seed.deletionScheduledAtISO ? new Date(seed.deletionScheduledAtISO) : null,
    targetExam: seed.targetExam,
    targetExams: seed.targetExams,
    currentGrade: seed.currentGrade,
    educationLevelSlug: seed.educationLevelSlug,
    educationLevel: seed.educationLevel,
    schoolName: seed.schoolName,
    audienceSegment: (seed.audienceSegment as User['audienceSegment']) ?? null,
  };
}

export function AuthProvider({
  children,
  seededUser,
}: {
  children: ReactNode;
  /**
   * Server-resolved user from the aks_session cookie (see lib/server-auth.ts), or null for a
   * signed-out visitor / unreachable API.
   *
   * When present the provider skips the blocking LoadingScreen entirely: `user` is populated
   * on the very first render and `isLoading` starts false, so pages issue their data fetches
   * immediately instead of queueing behind Firebase's session restore (profiled at ~1.3s on
   * staging, ~780ms of it a single identitytoolkit round-trip).
   *
   * onAuthStateChanged still runs: if Firebase resolves to a different user its handler
   * overwrites this seed, and if it resolves to none the session cookie is asked again
   * (resolveCookieUser) and decides whether this seed stays.
   */
  seededUser?: SeededUser | null;
}) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(() => seedToUser(seededUser));

  // Attaches the signed-in user to every Sentry event. Without this GlitchTip stored only
  // a Cloudflare edge IP, so "how many people hit this error" was unanswerable: 455
  // hydration events looked like 18 users when that was really 18 edge nodes.
  // Id only, deliberately — it answers the count question without widening PII.
  useEffect(() => {
    Sentry.setUser(user ? { id: user.id } : null);
  }, [user?.id]);
  // The server seed (when present) already carries the extended profile fields, so the
  // post-sync getProfile() below would re-fetch what we are already rendering. Holds the
  // seeded user's id so the skip only applies to THAT user — a sign-in as somebody else
  // still enriches normally.
  const seededProfileIdRef = useRef<string | null>(seededUser?.id ?? null);
  const [isLoading, setIsLoading] = useState(!seededUser);
  const [isFirebaseResolved, setIsFirebaseResolved] = useState(false);
  // reload() mutates the Firebase user in place, so firebaseUser keeps its identity and React
  // would not re-render consumers reading firebaseUser.emailVerified. Bumping this forces a
  // new context value.
  const [firebaseUserRevision, setFirebaseUserRevision] = useState(0);
  // The (auth) layout nests a second AuthProvider inside the root layout's, and both hold the
  // SAME Firebase user object. A reload() started under one of them (the verify page) flips
  // emailVerified for both, but only that provider's revision changes, so a consumer under the
  // other (the verification banner in the root layout) kept showing the old value. Firebase
  // notifies ID-token listeners after every reload, so each provider listens and bumps its own
  // revision when the flag it last saw has changed.
  const lastEmailVerifiedRef = useRef<boolean | null>(null);
  useEffect(
    () =>
      onIdTokenChanged(auth, (current) => {
        const next = current ? current.emailVerified : null;
        if (lastEmailVerifiedRef.current === next) return;
        lastEmailVerifiedRef.current = next;
        setFirebaseUserRevision((n) => n + 1);
      }),
    [],
  );
  const [redirectSignInCompleted, setRedirectSignInCompleted] = useState(false);
  const [redirectSignInErrorCode, setRedirectSignInErrorCode] = useState<string | null>(null);
  // Holds the heartbeat's stop function, not a timer id — the loop can also stop itself
  // once the server has conclusively rejected the session (see lib/device-heartbeat.ts).
  const heartbeatRef = useRef<(() => void) | null>(null);
  // Set by registerWithEmail immediately after it corrects `user.name` (see below).
  // onAuthStateChanged's own setUser call, below, can run either before or after that
  // correction — updateProfile(displayName) is not synchronized with the auth-state event,
  // so `firebaseUser.displayName` can still read null when the listener builds its user
  // object. If the listener runs AFTER the correction, this ref lets it recover the real
  // name instead of clobbering it back to null. Keyed by uid: in the listener-first
  // ordering the ref is set after the listener already ran, so it persists for the
  // provider's lifetime — the uid guard ensures a later auth-state change for a DIFFERENT
  // user (e.g. cross-tab signOut, then same-tab login as someone else) can never consume it.
  const pendingRegistrationNameRef = useRef<{ uid: string; name: string } | null>(null);

  // Signup-consent capture (fast-follow): registerWithEmail's createUserWithEmailAndPassword
  // call triggers this SAME provider's onAuthStateChanged listener nearly synchronously —
  // both it and registerWithEmail's own explicit syncUser call below race to create the
  // backend User row, and only the winner's request body is what actually lands in Postgres
  // (see auth.controller.ts's P2002 handling: the loser is just re-fetched, side-effect-free).
  // The listener's call has no way to know the register form's checkbox state on its own, so
  // whichever one wins would otherwise silently drop the user's choice about half the time.
  // Set synchronously before the race begins; both racing syncUser calls read it, so the
  // value reaches the DB regardless of which request wins. Only meaningful for THIS tab's
  // in-flight registration — a later login's syncUser calls also read it, but the backend
  // ignores marketingConsent entirely for an existing user (see auth.controller.ts), so a
  // stale leftover value here is inert.
  const pendingMarketingConsentRef = useRef<boolean | null>(null);
  // Birth year counterpart to pendingMarketingConsentRef, same race and same
  // rationale (2026-09-28 marketing-consent plan): only ever set alongside a
  // `true` marketingConsent from the register form's revealed field, so
  // whichever syncUser call wins the race still carries it.
  const pendingBirthYearRef = useRef<number | undefined>(undefined);

  // Complete a pending signInWithRedirect flow (see isStandaloneOrInAppBrowser / loginWithGoogle
  // below). Must run on every mount: after the redirect round trip this page reloads fresh, so
  // this is the ONLY place that can pick the result up. Without this, a redirect-based Google
  // sign-in silently does nothing — no error, no signed-in user, no navigation onward.
  useEffect(() => {
    let cancelled = false;

    getRedirectResult(auth)
      .then(async (result) => {
        if (cancelled || !result) return;

        try {
          // Eagerly mint the aks_session cookie/sync the backend record here (mirrors
          // loginWithGoogle's popup path) instead of waiting on onAuthStateChanged below —
          // we're about to signal completion so a caller (e.g. the login page) can navigate
          // away immediately, and that navigation must not race a not-yet-minted session.
          const token = await result.user.getIdToken();
          await establishSession(token);
          await AuthService.syncUser(token, {
            firebaseUid: result.user.uid,
            email: result.user.email,
            name: result.user.displayName,
            authProvider: 'GOOGLE',
          });
        } catch (syncError) {
          // Non-fatal: the user IS signed into Firebase; onAuthStateChanged below still fires
          // and retries this same sync. Log so it's visible, but don't block completion on it.
          console.error('Failed to sync user after redirect sign-in:', syncError);
        }

        if (!cancelled) setRedirectSignInCompleted(true);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Redirect sign-in failed:', error);
        const code = (error as { code?: string } | null)?.code ?? 'auth/redirect-sign-in-failed';
        setRedirectSignInErrorCode(code);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    // Counts auth-state events so the signed-out branch, which awaits the cookie check, can
    // tell that a newer event (a sign-in) has already been handled and must not be undone.
    let authEvent = 0;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const thisEvent = ++authEvent;
      setFirebaseUser(firebaseUser);
      
      if (firebaseUser) {
        signedOutHere = false;
        // Reads (and clears) the registration-name correction pending for THIS uid; null for
        // any other user. Must be evaluated at setUser time, not when this callback starts:
        // registerWithEmail may write the ref while our own syncUser call is still in flight.
        const takePendingRegistrationName = (): string | null => {
          const pending = pendingRegistrationNameRef.current;
          if (!pending || pending.uid !== firebaseUser.uid) return null;
          pendingRegistrationNameRef.current = null;
          return pending.name;
        };

        // Get user data from your API
        try {
          const token = await firebaseUser.getIdToken();
          // Bootstrap heal (design §5.2): every auth-state change with a signed-in Firebase
          // user re-checks whether the backend session cookie exists, and mints one if not.
          // Idempotent — a sign-in path below already called establishSession explicitly, so
          // this is normally a no-op; it only does real work for a stale/missing/expired
          // cookie or an in-flight rollout window.
          //
          // Runs concurrently with syncUser below (both only need `token`): the session-mint
          // endpoint only verifies the Firebase token and sets a cookie, it never reads or
          // writes the User table, so it has no ordering dependency on syncUser having
          // created/updated the row.
          const sessionHealPromise = (async () => {
            if (await isSessionCookieMissing()) {
              await establishSession(token);
            }
          })();

          // syncUser is an upsert — creates the DB record if missing, updates if exists.
          // authProvider is derived from Firebase's own provider data (not hardcoded) so
          // this fallback sync — the ONLY sync call that runs for a redirect-based Google
          // sign-in, since loginWithGoogle's own explicit call never resumes after the
          // full-page navigation — still records 'GOOGLE' correctly instead of mislabeling
          // every redirect login as 'EMAIL'.
          const authProvider: 'GOOGLE' | 'EMAIL' =
            firebaseUser.providerData[0]?.providerId === 'google.com' ? 'GOOGLE' : 'EMAIL';
          const [, userData] = await Promise.all([
            sessionHealPromise,
            AuthService.syncUser(token, {
              firebaseUid: firebaseUser.uid,
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              authProvider,
              // See pendingMarketingConsentRef's doc comment above — this call races
              // registerWithEmail's own explicit one for a brand-new user, so it must
              // carry the same value regardless of which one wins.
              marketingConsent: pendingMarketingConsentRef.current ?? undefined,
              birthYear: pendingBirthYearRef.current,
            }),
          ]);

          // Consume this uid's pending registration correction (if any) exactly here — see
          // takePendingRegistrationName above for why it can't be read earlier.
          const pendingRegistrationName = takePendingRegistrationName();

          const resolvedId = userData?.id || firebaseUser.uid;

          // Functional form on purpose: this callback is registered once, so reading `user`
          // from its closure would see the mount-time value forever. Merging rather than
          // replacing matters because a seeded render already holds the extended profile
          // fields, and a bare object here would blank them for a frame.
          setUser(prev => ({
            ...(prev && prev.id === resolvedId
              ? {
                  targetExam: prev.targetExam,
                  targetExams: prev.targetExams,
                  currentGrade: prev.currentGrade,
                  educationLevelSlug: prev.educationLevelSlug,
                  educationLevel: prev.educationLevel,
                  schoolName: prev.schoolName,
                  audienceSegment: prev.audienceSegment,
                }
              : {}),
            id: userData?.id || firebaseUser.uid,
            email: firebaseUser.email,
            // Prefer the backend's name, then this uid's same-session registration
            // correction still pending recovery, then whatever Firebase has locally.
            name: userData?.name || pendingRegistrationName || firebaseUser.displayName,
            photoURL: userData?.avatarUrl || firebaseUser.photoURL,
            role: userData?.role || 'STUDENT',
            onboardingCompleted: userData?.onboardingCompleted ?? false,
            accountStatus: userData?.status || 'ACTIVE',
            deletionScheduledAt: userData?.deletionScheduledAt ? new Date(userData.deletionScheduledAt) : null,
          }));

          // Fetch full profile non-blocking — enriches user state with onboarding fields
          // without delaying the loading screen. Components see these fields shortly after mount.
          //
          // Skipped when the server seed already delivered these fields for this same user:
          // the data is identical (both are GET /v1/users/me) and it was already rendered, so
          // the request bought nothing but a second round-trip after first paint.
          if (seededProfileIdRef.current !== resolvedId) {
          ProfileService.getProfile()
            .then(profile => {
              setUser(prev => prev ? {
                ...prev,
                targetExam: profile?.profile?.targetExam ?? undefined,
                targetExams: profile?.profile?.targetExams ?? undefined,
                currentGrade: profile?.student?.currentGrade ?? undefined,
                educationLevelSlug: profile?.student?.educationLevelSlug ?? null,
                educationLevel: profile?.student?.educationLevel ?? null,
                schoolName: profile?.student?.schoolName ?? undefined,
                audienceSegment: profile?.profile?.audienceSegment ?? null,
              } : prev);
            })
            .catch(() => { /* non-critical — personalization degrades gracefully */ });
          }

          // Start device heartbeat (every 2 minutes)
          heartbeatRef.current?.();
          heartbeatRef.current = null;
          const deviceId = getDeviceId();
          if (deviceId) {
            heartbeatRef.current = startDeviceHeartbeat({
              deviceId,
              endpoint: '/api/proxy/devices/heartbeat',
              getToken: (forceRefresh) => firebaseUser.getIdToken(forceRefresh),
            });
          }
        } catch (error) {
          const code = (error as { code?: string } | null)?.code;
          // Both codes mean the backend has NO User row for this Firebase account — falling
          // through to the optimistic setUser below (as any OTHER thrown error still does)
          // would strand the session: signed into Firebase, invisible to the backend, every
          // authenticated call 401ing forever. This is the listener's OWN syncUser retry
          // (line ~180 above, not registerWithEmail's), so a fresh email registration that
          // hits EMAIL_SIGNUP_UNAVAILABLE can reach here too, racing registerWithEmail's own
          // cleanup — signing out is idempotent either way.
          // signOut touches IndexedDB, which WebKit can tear down mid-session. Swallow its
          // rejection so it can't escape this floating listener callback AND skip the redirect —
          // stranding the session is the exact outcome the note above is guarding against.
          if (code === 'REGISTRATION_DISABLED') {
            await signOutHere().catch(() => {});
            window.location.href = '/login?error=registration_disabled';
            return;
          }
          if (code === 'EMAIL_SIGNUP_UNAVAILABLE') {
            await signOutHere().catch(() => {});
            window.location.href = '/login?error=email_signup_unavailable';
            return;
          }
          // The bootstrap heal could not mint a session and never will with this credential:
          // the API mints with checkRevoked=true, and a force-refreshed ID token keeps its
          // original auth_time, so only a real sign-in produces a mintable token. Falling
          // through would render a signed-in-looking page whose every fetch 401s. Skip it
          // when we're already in the sign-in flow — that page IS the fix, not a loop.
          if (error instanceof SessionMintError && error.status === 401) {
            if (!AUTH_FLOW_PATHS.some((p) => window.location.pathname.startsWith(p))) {
              await signOutHere().catch(() => {});
              window.location.href = '/login?error=session_expired';
              return;
            }
          }
          console.error('Error fetching user data:', error);
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            name: takePendingRegistrationName() || firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            role: 'STUDENT',
            onboardingCompleted: false,
            accountStatus: 'ACTIVE',
            deletionScheduledAt: null,
          });
        }
      } else {
        // No Firebase session on this origin. That is not the same as signed out: Firebase's
        // storage is per origin and can be evicted, while the apex aks_session cookie is what
        // the API authorizes on. So the cookie decides, as in test-engine and course-engine.
        // Blanking a user the server still serves left the header on its "Pengguna" fallback
        // and every page that waits for `user` on its loading skeleton.
        //
        // The same call sweeps a cookie that OUTLIVED its Firebase session (see
        // resolveCookieUser): without that, middleware keeps admitting a dead cookie and the
        // user bounces between the app and /login. A sign-out this app started itself skips
        // the cookie and ends signed out (see signedOutHere).
        const cookieUser = signedOutHere ? null : await resolveCookieUser();
        if (thisEvent !== authEvent) return;
        // Same person as the seed: keep the object already rendered, extras included.
        setUser((prev) => (cookieUser && prev?.id === cookieUser.id ? prev : seedToUser(cookieUser)));
        heartbeatRef.current?.();
        heartbeatRef.current = null;
      }

      setIsLoading(false);
      setIsFirebaseResolved(true);
    });

    return () => {
      unsubscribe();
      heartbeatRef.current?.();
      heartbeatRef.current = null;
    };
  }, []);

  /**
   * Serialises Google sign-in attempts. Starting a second popup operation while one is
   * still live makes Firebase cancel the first, and that cancellation rejects an
   * operation whose pendingPromise may never have been set — the SDK then throws
   * "INTERNAL ASSERTION FAILED: Pending promise was never set" from inside its own auth
   * event handler, outside this promise chain, so it escapes as an unhandled rejection
   * that no caller's catch can reach (seen in prod on Android Chrome, AKSADEMIA-DASHBOARD-4).
   * A duplicate call awaits the original attempt instead of racing a second popup, so
   * callers still observe the real result rather than a silent no-op.
   */
  const googleSignInRef = useRef<Promise<void> | null>(null);

  // No deps: closes only over module-level imports (auth, googleProvider, shouldUseRedirect,
  // establishSession, AuthService) and no component state. Must stay referentially stable so
  // loginWithGoogle below (and, transitively, the memoized context value) doesn't churn.
  const runGoogleSignIn = useCallback(async () => {
    try {
      if (shouldUseRedirect()) {
        // signInWithPopup is unreliable here (see shouldUseRedirect in
        // @/lib/should-use-redirect): the promise can hang or never resolve on standalone
        // PWAs, mobile browsers, and in-app webviews. signInWithRedirect performs a full
        // top-level navigation instead, which every browser context supports; completion is
        // picked up by the getRedirectResult effect above on the next load of this page. This
        // call never returns normally (the navigation happens before the returned promise
        // would settle), so there is nothing further to do here.
        await signInWithRedirect(auth, googleProvider);
        return;
      }

      const result = await signInWithPopup(auth, googleProvider);
      const token = await result.user.getIdToken();

      // Eagerly mint the aks_session cookie so it's readable (server-side, by every app
      // under .aksademia.com) the moment the browser lands on another app (e.g. landing-page)
      // after a redirect — onAuthStateChanged fires async and may lose the race against
      // window.location.href assignments.
      await establishSession(token);

      // Sync user with backend
      await AuthService.syncUser(token, {
        firebaseUid: result.user.uid,
        email: result.user.email,
        name: result.user.displayName,
        authProvider: 'GOOGLE',
      });
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }, []);

  // Deps: [runGoogleSignIn] — stable per above, so this stays stable too. Closes over
  // googleSignInRef (a ref, always stable) and no other component state.
  const loginWithGoogle = useCallback(async (): Promise<void> => {
    const inFlight = googleSignInRef.current;
    if (inFlight) return inFlight;

    const attempt = runGoogleSignIn();
    googleSignInRef.current = attempt;
    try {
      await attempt;
    } finally {
      googleSignInRef.current = null;
    }
  }, [runGoogleSignIn]);

  // No deps: closes only over module-level imports and no component state.
  const loginWithEmail = useCallback(async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();

      // Eagerly mint the session (same race-condition fix as loginWithGoogle above)
      await establishSession(token);

      await AuthService.syncUser(token, {
        firebaseUid: result.user.uid,
        email: result.user.email,
        name: result.user.displayName,
        authProvider: 'EMAIL',
      });
    } catch (error) {
      console.error('Email login error:', error);
      throw error;
    }
  }, []);

  // No deps: closes over module-level imports, pendingRegistrationNameRef (a ref, stable),
  // and setUser (setState identity is stable across renders) — no component state read.
  const registerWithEmail = useCallback(async (
    email: string,
    password: string,
    name: string,
    marketingConsent: boolean,
    birthYear: number | undefined,
    agreeTerms: boolean,
  ): Promise<{ marketingConsentRefusalReason?: 'NOT_ADULT' }> => {
    // Hoisted so the catch below only attempts Firebase cleanup for a user THIS call
    // created — createUserWithEmailAndPassword's OWN failures (email-already-in-use,
    // weak-password, ...) throw before this is ever set, and must not trigger it.
    let createdUser: FirebaseUser | null = null;
    let syncedOk = false;
    // Set BEFORE createUserWithEmailAndPassword — see pendingMarketingConsentRef's doc
    // comment: the onAuthStateChanged listener's own syncUser call can win the race to
    // create the backend row, so it must already be able to read this by the time that
    // happens (its trigger fires nearly synchronously with the call below).
    pendingMarketingConsentRef.current = marketingConsent;
    pendingBirthYearRef.current = birthYear;
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      createdUser = result.user;

      // Update profile with name
      await updateProfile(result.user, { displayName: name });

      const token = await result.user.getIdToken();

      // Eagerly mint the session — registration is a sign-in path too. onAuthStateChanged's
      // bootstrap heal would catch this eventually, but minting it here keeps the cookie
      // available immediately if the caller navigates away before that listener fires.
      await establishSession(token);

      // Send branded verification email via our API (non-blocking)
      fetch('/api/proxy/auth/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      }).catch(() => console.warn('Failed to send verification email'));

      const userData = await AuthService.syncUser(token, {
        firebaseUid: result.user.uid,
        email: result.user.email,
        name: name,
        authProvider: 'EMAIL',
        marketingConsent,
        birthYear,
        agreeTerms,
      });

      // syncUser() throws for every KNOWN blocking code (REGISTRATION_DISABLED,
      // EMAIL_SIGNUP_UNAVAILABLE — caught below), so `null` here means a backend failure it
      // couldn't classify (5xx, malformed response, ...). Either way there is no backend
      // User row: proceeding would strand the caller exactly like the classified case does,
      // so this is deliberately NOT treated as a soft/ignorable failure.
      if (!userData) {
        throw new Error('Gagal membuat akun. Silakan coba lagi dalam beberapa saat.');
      }
      // Past this point the backend User row EXISTS, so the Firebase account must survive
      // any later failure — deleting it would destroy a real account and orphan its row.
      // Nothing below currently throws, but this keeps the cleanup correct if that changes.
      syncedOk = true;

      // onAuthStateChanged (above) races this function's updateProfile(displayName) call and
      // typically fires before it commits, so it captures firebaseUser.displayName = null and
      // its own setUser never has the real name. This is the authoritative correction, using
      // the name the user actually typed. Also stash it in a uid-keyed ref so the listener can
      // recover it if its setUser call happens to run AFTER this one instead of before (prev
      // would still be null in that ordering, making the object-spread below a no-op).
      pendingRegistrationNameRef.current = { uid: result.user.uid, name };
      setUser(prev => (prev ? { ...prev, name } : prev));
      return { marketingConsentRefusalReason: userData.marketingConsentRefusalReason };
    } catch (error) {
      console.error('Registration error:', error);
      // A Firebase account can now exist with no matching backend User row (sync failed
      // after createUserWithEmailAndPassword succeeded). It is tempting to delete it
      // whenever sync didn't confirm success, so the same email can be retried cleanly —
      // but a `null`/unclassified failure here does NOT prove the row is missing. The
      // onAuthStateChanged listener (above) runs its OWN independent syncUser call and can
      // WIN row creation over this one (auth.controller.ts backfill comment, ~line 310):
      // registerWithEmail's own sync can then 500 (e.g. a P2002 from that race), a
      // gateway timeout can land AFTER the backend commits the create, or updateProfile
      // can reject after the listener already created the row — every one of those
      // surfaces here as a `null`/unclassified error, indistinguishable from "no row was
      // ever created" without asking the backend again.
      //
      // Only REGISTRATION_DISABLED and EMAIL_SIGNUP_UNAVAILABLE are safe to delete on:
      // both are thrown exclusively from auth.controller.ts's `if (!user)` branch, which
      // means the backend just PROVED no row exists (a row found by email skips that
      // branch entirely) and is refusing to create one. Every other failure — network
      // blip, unclassified 5xx, updateProfile rejecting — signs out WITHOUT deleting:
      // destroying a real account is far worse than leaving a derelict Firebase user
      // behind, and syncUser rebinds an existing row by email on the next attempt anyway
      // (auth.controller.ts:126-136), so nothing is permanently stuck either way.
      const errorCode = (error as { code?: string } | null)?.code;
      if (createdUser && !syncedOk) {
        if (isFatalSyncErrorCode(errorCode)) {
          try {
            await createdUser.delete();
          } catch (cleanupError) {
            // delete() can refuse with auth/requires-recent-login — vanishingly unlikely
            // seconds after creation, but if it happens, sign-out is the fallback: the
            // caller's session is still killed even if the derelict account lingers.
            console.error('Failed to delete orphaned Firebase user, signing out instead:', cleanupError);
            await signOutHere().catch(() => {});
          }
        } else {
          await signOutHere().catch(() => {});
        }
      }
      throw error;
    }
  }, []);

  // No deps: closes over module-level imports and setUser (stable) only.
  const logout = useCallback(async () => {
    try {
      // Revoke + clear the server-side session BEFORE signOut(auth) (design §4.4). Clearing
      // the apex cookie logs the user out of every B2C app (test-engine, course-engine,
      // landing) at once, not just dashboard. Best-effort: a network hiccup reaching
      // /api/auth/session must not trap the user in a "can't log out" state, so its failure
      // doesn't block the local Firebase signOut that follows.
      await endSession().catch((error) => {
        console.error('Failed to end server session:', error);
      });
      await signOutHere();
      setUser(null);
      const landingUrl = process.env.NEXT_PUBLIC_LANDING_URL || '/';
      window.location.href = landingUrl;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }, []);

  // No deps: closes over module-level imports and setUser (stable) only. Unlike logout this
  // must not revoke: the cookie is unusable for what the user wants to do here, not
  // compromised, and revoking would sign out their sessions on every other device.
  const signInAgain = useCallback(async (returnTo: string) => {
    await endSession({ revoke: false });
    // The cookie is gone now. A local sign-out that throws (storage unavailable) must not stop
    // the navigation: every retry would fail the same way with nothing left to retry for.
    await signOutHere().catch((error) => {
      console.error('Failed to sign out of Firebase locally:', error);
    });
    setUser(null);
    window.location.href = `/login?redirect=${encodeURIComponent(returnTo)}`;
  }, []);

  // Deps: [firebaseUser] — reads it directly, so must recreate when it changes. That's fine:
  // firebaseUser also sits directly in the context value, so the memo below already
  // invalidates on the same render this function's identity would change.
  const getIdToken = useCallback(async () => {
    if (!firebaseUser) return null;
    return firebaseUser.getIdToken();
  }, [firebaseUser]);

  // No deps: closes over setUser (stable) only.
  const markOnboardingComplete = useCallback(() => {
    setUser(prev => prev ? { ...prev, onboardingCompleted: true } : prev);
  }, []);

  // No deps: closes over setUser (stable) only; `data` comes in as a call argument, not a closure.
  const updatePersonalization = useCallback((data: { audienceSegment?: AudienceSegment | null; targetExam?: string | null; targetExams?: string[] }) => {
    setUser(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        ...(data.audienceSegment !== undefined ? { audienceSegment: data.audienceSegment } : {}),
        ...(data.targetExam !== undefined ? { targetExam: data.targetExam ?? undefined } : {}),
        ...(data.targetExams !== undefined ? { targetExams: data.targetExams } : {}),
      };
    });
  }, []);

  // Deps: [firebaseUser] — reads it directly (guard + getIdToken()).
  const cancelDeletion = useCallback(async () => {
    if (!firebaseUser) throw new Error('Not authenticated');
    const token = await firebaseUser.getIdToken();
    const res = await fetch('/api/proxy/users/me/cancel-deletion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.error || 'Gagal membatalkan penghapusan akun');
    }
    // Update local state so UI reflects the cancellation immediately
    setUser(prev => prev ? { ...prev, accountStatus: 'ACTIVE', deletionScheduledAt: null } : prev);
  }, [firebaseUser]);

  // Deps: [firebaseUser] — reads it directly (guard + getIdToken()).
  const resendVerificationEmail = useCallback(async () => {
    if (!firebaseUser) throw new Error('Not authenticated');
    const token = await firebaseUser.getIdToken();
    const res = await fetch('/api/proxy/auth/send-verification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ResendVerificationError(body?.error || 'Gagal mengirim email verifikasi', res.status);
    }
  }, [firebaseUser]);

  // Deps: [firebaseUser], read directly. No-op when signed out (e.g. /auth/verify opened
  // without a session). Errors propagate; callers treat the refresh as best effort.
  const refreshEmailVerified = useCallback(async () => {
    if (!firebaseUser) return;
    await reload(firebaseUser);
    setFirebaseUserRevision((n) => n + 1);
  }, [firebaseUser]);

  // Memoized so setUser/setFirebaseUser calls that don't actually change these values (or
  // change only one of them) don't hand every useAuth() consumer a brand-new object identity,
  // which would re-render all of them. Must be called unconditionally (before the isLoading
  // early return below) to satisfy the Rules of Hooks — hooks can't be skipped on some renders.
  // Deps list every value/function read from the object below; all functions are individually
  // memoized above so this only actually changes when user/firebaseUser/isLoading/the
  // redirect-sign-in flags change.
  const contextValue = useMemo<AuthContextType>(() => ({
    user,
    firebaseUser,
    isLoading,
    isFirebaseResolved,
    isAuthenticated: !!user,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    logout,
    signInAgain,
    getIdToken,
    redirectSignInCompleted,
    redirectSignInErrorCode,
    markOnboardingComplete,
    resendVerificationEmail,
    refreshEmailVerified,
    cancelDeletion,
    updatePersonalization,
    // firebaseUserRevision is not read here but must invalidate the memo: see refreshEmailVerified.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    user,
    firebaseUser,
    isLoading,
    isFirebaseResolved,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    logout,
    signInAgain,
    getIdToken,
    redirectSignInCompleted,
    redirectSignInErrorCode,
    markOnboardingComplete,
    resendVerificationEmail,
    refreshEmailVerified,
    firebaseUserRevision,
    cancelDeletion,
    updatePersonalization,
  ]);

  if (isLoading) return <LoadingScreen />;

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
